/**
 * Hook — Polling des discussions non lues.
 * - Interroge /api/discussions/unread toutes les 30s
 * - Déclenche un toast quand de nouveaux messages arrivent
 * - Expose totalUnread pour afficher un badge
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { getDiscussionsUnread } from '../api';

const POLL_INTERVAL = 30_000; // 30 secondes

export function useUnreadDiscussions(enabled = true) {
  const [unread, setUnread]         = useState([]);  // liste des threads non-lus
  const [totalUnread, setTotal]     = useState(0);
  const prevThreadsRef              = useRef({});     // { thread_id: unread_count }
  const isFirstLoad                 = useRef(true);

  const poll = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await getDiscussionsUnread();
      const data = res.data || [];

      const newMap = {};
      data.forEach(t => { newMap[t.thread_id] = t.unread_count; });

      // Détecter les nouveaux messages (pas au premier chargement)
      if (!isFirstLoad.current) {
        data.forEach(t => {
          const prev = prevThreadsRef.current[t.thread_id] ?? 0;
          if (t.unread_count > prev) {
            toast.message(`💬 ${t.record_label || 'Discussion'}`, {
              description: t.last_auteur
                ? `${t.last_auteur} : ${(t.last_corps || '').slice(0, 80)}`
                : (t.last_corps || '').slice(0, 100),
              duration: 6000,
            });
          }
        });
      }

      prevThreadsRef.current = newMap;
      isFirstLoad.current    = false;
      setUnread(data);
      setTotal(data.reduce((s, t) => s + (t.unread_count || 0), 0));
    } catch {
      // silencieux — pas de spam d'erreurs réseau
    }
  }, [enabled]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [poll]);

  return { unread, totalUnread, refresh: poll };
}
